import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../shared/api/client';

/**
 * Public status of the data sources (section 23.9).
 *
 * A source that refuses requests from outside its own country is shown as
 * "geo-blocked" rather than "down": it is working, we simply cannot reach it
 * from here. Collapsing the two would either cry wolf about healthy portals or
 * hide a real gap — and section 3.5 requires the gap to be visible.
 */
interface HealthRow {
  code: string;
  name: string;
  status: string;
  geoBlocked: boolean;
  avgLatencyMs: number | null;
  needsOwnerAction: boolean;
  hasAdapter: boolean;
}

export function StatusPage(): React.ReactNode {
  const { t } = useTranslation();

  const health = useQuery({
    queryKey: ['sources-health'],
    queryFn: ({ signal }) => apiFetch<HealthRow[]>('/sources/health', { signal }),
    staleTime: 60_000,
  });

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <h1 className="text-2xl font-bold">{t('footer.status')}</h1>
      <p className="mt-2 text-sm text-[var(--color-muted-fg)]">
        {t('status.subtitle')}
      </p>

      {health.isLoading && (
        <p className="mt-6 text-sm text-[var(--color-muted-fg)]">{t('common.loading')}</p>
      )}
      {health.isError && (
        <p className="mt-6 text-sm text-[var(--color-muted-fg)]">{t('errors.generic')}</p>
      )}

      {health.data && (
        <table className="mt-6 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] text-left">
              <th scope="col" className="py-2 pr-4 font-medium">{t('lookup.sourcesTitle')}</th>
              <th scope="col" className="py-2 pr-4 font-medium">{t('sourceStatus.title')}</th>
              <th scope="col" className="py-2 font-medium">{t('sourceStatus.avgLatency')}</th>
            </tr>
          </thead>
          <tbody>
            {health.data.map((row) => {
              // `degraded` has two causes and they mean different things to a
              // reader: a geo-blocked portal is healthy but refuses us, while a
              // slow one is genuinely degraded. Section 3.5 requires the
              // distinction to be visible rather than collapsed.
              const state = !row.hasAdapter
                ? 'unconfigured'
                : row.needsOwnerAction
                  ? 'needsAction'
                  : row.geoBlocked
                    ? 'geoBlocked'
                    : row.status;
              return (
                <tr key={row.code} className="border-b border-[var(--color-border)]">
                  <td className="py-2 pr-4">
                    <span className="block">{row.name}</span>
                    <span className="font-mono text-xs text-[var(--color-muted-fg)]">{row.code}</span>
                  </td>
                  <td className="py-2 pr-4">
                    {t(`sourceStatus.${state}`, { defaultValue: row.status })}
                  </td>
                  <td className="py-2 font-mono text-xs text-[var(--color-muted-fg)]">
                    {row.avgLatencyMs === null ? '—' : `${row.avgLatencyMs} ms`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
